import {
  Board,
  pageFailure,
  QuoteState,
} from "../../../../../reducers/uploadedBoard";
import useSendBoardEvent, { BoardEvent } from "../state-machine/sendBoardEvent";
import { useShoppingCartSummary } from "../../shoppingCart";
import { useCustomConfirm } from "../../../alerts";
import { useCallback, useContext } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../state-machine/reviewBoardFlowLoader";

export const useGetAlertMessage = () => {
  return useCallback((board: Board) => {
    if (board.quote.state === QuoteState.ORDER_RECEIVED) {
      return null;
    }

    if (board.quote.state == QuoteState.IN_SHOPPING_CART) {
      if (board.quote.requested_custom_quote) {
        return `Editing this board will remove it from your cart. You will need to request a new custom quote from a FlashPCB engineer before adding to your cart again.\n\nContinue?`;
      }
      return "Editing this board will remove it from your cart.\n\nContinue?";
    } else if (board.quote.requested_custom_quote) {
      if (board.quote.total === null) {
        return `Editing this board will cancel the custom quote request.\n\nContinue?`;
      } else {
        return `Editing this board will revoke your custom quote. You might need to request a new quote from a FlashPCB engineer to see a price again.\n\nContinue?`;
      }
    }
    return null;
  }, []);
};

export function useSendUpdateEvent(undoUpdate?: () => void) {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const boardContext = useAppSelector((state) =>
    boardId ? state.uploadedBoard.boards[boardId] : null,
  );
  const quoteState = boardContext?.board?.quote.state;
  const board = boardContext?.board ?? null;
  const dispatch = useAppDispatch();
  const errorHandler = useCallback(() => {
    if (boardId) {
      dispatch(pageFailure({ boardId }));
    }
  }, [dispatch, boardId]);
  const { sendBoardEvent, loading } = useSendBoardEvent(errorHandler);
  const { refreshShoppingCartSummary } = useShoppingCartSummary();
  const customConfirm = useCustomConfirm();
  const getAlertMessage = useGetAlertMessage();

  const performUpdate = useCallback(
    async (event: BoardEvent) => {
      if (boardId) {
        await sendBoardEvent(boardId, event).then(() => {
          if (quoteState == QuoteState.IN_SHOPPING_CART) {
            void refreshShoppingCartSummary();
          }
        });
      }
    },
    [boardId, quoteState, refreshShoppingCartSummary, sendBoardEvent],
  );

  const sendUpdateEvent = useCallback(
    async (event: BoardEvent, onSuccess?: () => void) => {
      if (board !== null) {
        const confirmationMessage = getAlertMessage(board);

        if (confirmationMessage !== null) {
          customConfirm(
            confirmationMessage,
            async () => {
              await performUpdate(event);
              onSuccess?.();
            },
            () => {
              undoUpdate?.();
            },
          );
        } else {
          await performUpdate(event);
          onSuccess?.();
        }
      }
    },
    [board, customConfirm, undoUpdate, performUpdate, getAlertMessage],
  );

  return { sendUpdateEvent, loading };
}
